import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button } from '../components';
import { Search, User, MapPin, Calendar, CheckCircle, Clock, XCircle, Eye } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-hot-toast';

interface SearchRequest {
  id: string;
  employer_id: string;
  employer_first_name: string;
  employer_last_name: string;
  employer_email: string;
  employer_company?: string;
  search_criteria: any;
  status: 'pending' | 'in_progress' | 'fulfilled' | 'cancelled';
  created_at: string;
  updated_at: string;
  fulfilled_at?: string;
  belagom_search_team_enabled?: boolean;
  assigned_professional_id?: string;
}

export const AdminSearchRequestsPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'pending' | 'in_progress' | 'fulfilled' | 'cancelled'>('all');
  const [selectedSearchTeam, setSelectedSearchTeam] = useState<'all' | 'true' | 'false'>('all');
  const [selectedRequest, setSelectedRequest] = useState<SearchRequest | null>(null);
  const queryClient = useQueryClient();

  const { data: searchRequests, isLoading } = useQuery({
    queryKey: ['admin-search-requests', selectedStatus, selectedSearchTeam],
    queryFn: async () => {
      const params: any = {};
      if (selectedStatus !== 'all') params.status = selectedStatus;
      if (selectedSearchTeam !== 'all') params.belagom_search_team = selectedSearchTeam;
      const response = await api.get('/admin/search-requests', { params });
      return response.data.data as SearchRequest[];
    }
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status, assigned_professional_id }: { id: string; status: string; assigned_professional_id?: string }) => {
      const response = await api.patch(`/admin/search-requests/${id}/status`, {
        status,
        assigned_professional_id
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success(t('admin.searchRequests.statusUpdated'));
      queryClient.invalidateQueries({ queryKey: ['admin-search-requests'] });
      setSelectedRequest(null);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || t('admin.searchRequests.statusUpdateError'));
    }
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'fulfilled': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return t('admin.searchRequests.pending');
      case 'in_progress': return t('admin.searchRequests.inProgress');
      case 'fulfilled': return t('admin.searchRequests.fulfilled');
      case 'cancelled': return t('admin.searchRequests.cancelled');
      default: return status;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Filters */}
      <div className="mb-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.searchRequests.statusFilter')}</label>
          <div className="flex gap-2">
            {(['all', 'pending', 'in_progress', 'fulfilled', 'cancelled'] as const).map((status) => (
              <button
                key={status}
                onClick={() => setSelectedStatus(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedStatus === status
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {status === 'all' ? t('admin.searchRequests.all') : getStatusLabel(status)}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.searchRequests.searchTeam')}</label>
          <div className="flex gap-2">
            {(['all', 'true', 'false'] as const).map((value) => (
              <button
                key={value}
                onClick={() => setSelectedSearchTeam(value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedSearchTeam === value
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {value === 'all'
                  ? t('admin.searchRequests.all')
                  : value === 'true'
                  ? t('admin.searchRequests.searchTeamEnabled')
                  : t('admin.searchRequests.searchTeamDisabled')}
              </button>
            ))}
          </div>
        </div>
      </div>

      {isLoading ? (
        <Card>
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          </div>
        </Card>
      ) : searchRequests && searchRequests.length > 0 ? (
        <div className="space-y-4">
          {searchRequests.map((request) => (
            <Card key={request.id}>
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {request.employer_first_name} {request.employer_last_name}
                      </h3>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(request.status)}`}>
                        {getStatusLabel(request.status)}
                      </span>
                      {request.belagom_search_team_enabled && (
                        <span className="px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800">
                          {t('admin.searchRequests.searchTeamActive')}
                        </span>
                      )}
                    </div>
                    <div className="space-y-1 text-sm text-gray-600">
                      <div className="flex items-center">
                        <User className="w-4 h-4 mr-2" />
                        {request.employer_email}
                      </div>
                      {request.employer_company && (
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-2" />
                          {request.employer_company}
                        </div>
                      )}
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-2" />
                        {t('admin.searchRequests.createdAt')} {formatDate(request.created_at)}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedRequest(request)}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      {t('admin.searchRequests.details')}
                    </Button>
                    {request.status === 'pending' && (
                      <Button
                        size="sm"
                        onClick={() => updateStatusMutation.mutate({ id: request.id, status: 'in_progress' })}
                        loading={updateStatusMutation.isPending}
                      >
                        <Clock className="w-4 h-4 mr-1" />
                        {t('admin.searchRequests.startProcessing')}
                      </Button>
                    )}
                    {request.status === 'in_progress' && (
                      <Button
                        size="sm"
                        onClick={() => updateStatusMutation.mutate({ id: request.id, status: 'fulfilled' })}
                        loading={updateStatusMutation.isPending}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        {t('admin.searchRequests.markFulfilled')}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <div className="text-center py-12">
            <Search className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('admin.searchRequests.noRequests')}
            </h2>
            <p className="text-gray-600">
              {t('admin.searchRequests.noRequestsDesc')}
            </p>
          </div>
        </Card>
      )}

      {/* Detail Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-2xl font-bold text-gray-900">{t('admin.searchRequests.modalTitle')}</h2>
                <button
                  onClick={() => setSelectedRequest(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">{t('admin.searchRequests.employer')}</h3>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    <p><strong>{t('admin.searchRequests.name')}</strong> {selectedRequest.employer_first_name} {selectedRequest.employer_last_name}</p>
                    <p><strong>{t('admin.searchRequests.email')}</strong> {selectedRequest.employer_email}</p>
                    {selectedRequest.employer_company && (
                      <p><strong>{t('admin.searchRequests.company')}</strong> {selectedRequest.employer_company}</p>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">{t('admin.searchRequests.searchCriteria')}</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <pre className="text-sm whitespace-pre-wrap">
                      {JSON.stringify(selectedRequest.search_criteria, null, 2)}
                    </pre>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">{t('admin.searchRequests.status')}</h3>
                  <div className="flex items-center gap-4">
                    <span className={`px-3 py-1 rounded text-sm font-medium ${getStatusColor(selectedRequest.status)}`}>
                      {getStatusLabel(selectedRequest.status)}
                    </span>
                    <span className="text-sm text-gray-600">
                      {t('admin.searchRequests.createdAtLabel')} {formatDate(selectedRequest.created_at)}
                    </span>
                    {selectedRequest.fulfilled_at && (
                      <span className="text-sm text-gray-600">
                        {t('admin.searchRequests.fulfilledAt')} {formatDate(selectedRequest.fulfilled_at)}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex gap-3 pt-4 border-t">
                  {selectedRequest.status === 'pending' && (
                    <Button
                      onClick={() => {
                        updateStatusMutation.mutate({ id: selectedRequest.id, status: 'in_progress' });
                      }}
                      loading={updateStatusMutation.isPending}
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      {t('admin.searchRequests.startProcessing')}
                    </Button>
                  )}
                  {selectedRequest.status === 'in_progress' && (
                    <Button
                      onClick={() => {
                        updateStatusMutation.mutate({ id: selectedRequest.id, status: 'fulfilled' });
                      }}
                      loading={updateStatusMutation.isPending}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      {t('admin.searchRequests.markAsFulfilled')}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => setSelectedRequest(null)}
                  >
                    {t('admin.searchRequests.close')}
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};
